import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeIn, staggerContainer } from './utils/animations';

const NotFound = () => {
  // Set page title and status code for SEO
  useEffect(() => {
    document.title = "Page Not Found | forca.dev";
    
    // For SEO, attempt to send the correct HTTP status code
    if (typeof window.__REACT_ROUTER_CUSTOM_STATUS__ !== 'undefined') {
      window.__REACT_ROUTER_CUSTOM_STATUS__ = 404;
    }
  }, []);

  return (
    <motion.div
      variants={staggerContainer(0.1, 0.1)}
      initial="hidden"
      animate="show"
      className="min-h-screen flex items-center justify-center p-4 pt-20"
    >
      <div className="container max-w-4xl mx-auto">
        <div className="relative rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-950 to-slate-900 p-8 md:p-12 overflow-hidden">
          {/* Background gradient elements */}
          <div className="absolute -top-40 -left-40 h-80 w-80 rounded-full bg-gradient-to-br from-cyan-500/10 to-blue-500/5 blur-3xl"></div>
          <div className="absolute -bottom-40 -right-40 h-80 w-80 rounded-full bg-gradient-to-br from-blue-500/10 to-cyan-500/5 blur-3xl"></div>
          
          <div className="relative z-10 text-center">
            <motion.div 
              variants={fadeIn('down', 0.2)}
              className="inline-block mb-6"
            >
              <div className="flex justify-center">
                <div className="text-8xl md:text-9xl font-bold">
                  <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">404</span>
                </div>
              </div>
            </motion.div>
            
            <motion.h1 
              variants={fadeIn('up', 0.3)}
              className="text-2xl md:text-3xl font-bold text-white mb-4"
            >
              Page Not Found
            </motion.h1>
            
            <motion.p 
              variants={fadeIn('up', 0.4)}
              className="max-w-lg mx-auto text-slate-400 mb-8"
            >
              The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
            </motion.p>
            
            <motion.div 
              variants={fadeIn('up', 0.5)}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/" className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 p-px font-semibold text-white shadow-[0_1000px_0_0_hsl(0_0%_100%_/_0%)_inset] transition-colors hover:shadow-[0_1000px_0_0_hsl(0_0%_100%_/_2%)_inset]">
                <div className="relative rounded-xl bg-slate-950/50 px-6 py-3 transition-colors group-hover:bg-transparent">
                  <span className="relative flex items-center justify-center gap-2">
                    Back to Homepage
                    <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">
                      <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                    </svg>
                  </span>
                </div>
              </Link>
              
              <Link to="/contact" className="px-6 py-3 border border-slate-700 hover:border-cyan-500 text-slate-300 hover:text-cyan-500 rounded-xl transition-colors">
                Contact Support
              </Link>
            </motion.div>
            
            <motion.div 
              variants={fadeIn('up', 0.6)}
              className="mt-12 text-slate-500 text-sm"
            >
              <p>Looking for something specific? Try checking out these popular pages:</p>
              <div className="mt-4 flex flex-wrap justify-center gap-4">
                <a href="/#services" className="text-cyan-500 hover:text-cyan-400 transition-colors">Services</a>
                <a href="/#about" className="text-cyan-500 hover:text-cyan-400 transition-colors">About</a>
                <a href="/#pricing" className="text-cyan-500 hover:text-cyan-400 transition-colors">Pricing</a>
                <a href="/#faq" className="text-cyan-500 hover:text-cyan-400 transition-colors">FAQ</a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NotFound;